// Everything the UI can do. Each action returns { ok: boolean, msg: string } and mutates state.
import { eqDef, fieldDef, rigStats, bitFitsRig, stat, unitPrice, isStackable, isAvailable, yearOf, yf, ROLES, regionDef } from '../sim/defs.js';
import { addRig, addItem, addStock, stockQty } from './state.js';
import { createWell, wellRig, startRigUp, startDress, startBitChange, bestSpareBit, runCasingImpl, completeImpl, plugImpl, shootImpl, releaseRigImpl, beginDeepening, setDriveTouchImpl, takeBrakeImpl, ACTIVE_STATUSES, WOB_MIN, WOB_MAX, startStraighten, drillDay, ropAt, crewFactors } from '../sim/drilling.js';
import { sellOilImpl, buildStorageImpl, setTransportImpl, installLiftImpl, shutInImpl, setSellModeImpl } from '../sim/production.js';
import { hireImpl, hireFiremanImpl, fireImpl, assignCrewImpl, makeCrew, activeRigCrew, crewWage, roleName } from '../sim/crew.js';
import { takeLoanImpl, repayLoanImpl } from '../sim/finance.js';
import { surveyImpl, leaseImpl, sellLeaseImpl, fieldOpen } from '../sim/survey.js';
import { resolveImpl, assignGuardImpl, buildFortImpl, effectiveRisk, regionOfField, popDecision } from '../sim/security.js';
import { startResearchImpl, setFundingImpl, cancelResearchImpl, assignInventorImpl, resolveInventionImpl, eurekaImpl, rerollSetbackImpl, isUnlocked } from '../sim/research.js';
import { setSafetyBudgetImpl, respondWarningImpl, setBoilerSpacingImpl, buySafetyImpl, resolveCompImpl } from '../sim/safety.js';
import { resolveTankFireImpl } from '../sim/tankfire.js';
import { setFiringRateImpl, topUpBoilerImpl } from '../sim/boiler.js';
import { standDownImpl, recallImpl, mothballImpl, reactivateImpl, rigUnavailable } from '../sim/austerity.js';
import { buildRefineryImpl, addUnitImpl, setThroughputImpl, setFireTestImpl, buyCrudeImpl, routeCrudeImpl, sellProductImpl,
  setProductSellModeImpl, respondStandardOfferImpl, setAutoBuyCrudeImpl, setRefinerySafetyImpl, setBurnResiduumImpl } from '../sim/refining.js';
import { finishTimer } from '../sim/timers.js';
import { strataAt, stratumAt, reservoirsAt } from '../sim/geo.js';
import * as tokens from '../economy/tokens.js';
import { spend, fmt$, log } from '../sim/util.js';
// M2 rotary drilling (sim/rotary.js, mud.js, wellcontrol.js, pipe.js, cement.js, automation.js, fieldops.js)
import * as M2R from '../sim/m2actions.js';
// M2 economy & politics (sim/m2/*)
import { setProductionImpl, setHotOilImpl } from '../sim/m2/proration.js';
import { setPartnerImpl, payBribeImpl, buyInsuranceImpl, cancelInsuranceImpl, regionBlock } from '../sim/m2/politics.js';
import { runElogImpl, coreWellImpl } from '../sim/m2/geophysics.js';
import { preBuy, postBuy, buyLicenseImpl, fileSuitImpl, settleSuitImpl } from '../sim/m2/licensing.js';
import { M2_DECISION_KINDS, resolveM2Decision } from '../sim/m2/index.js';
// M3 economy & politics (sim/m3/*: lease rounds, concessions, participation, hedging, routes, safety case)
import * as M3E from '../sim/m3/econactions.js';
// M3 offshore operations (sim/m3/ops*: offshore, storms, platforms, logistics, charters, eor, megaprojects)
import * as M3O from '../sim/m3/opsactions.js';
import { shalePadSlot, shaleOnAssign, shaleBuyRoute } from '../sim/m4/shale.js'; // M4-A: shale pads & the completion/water shop tabs (inert < 2000)
import { M4W_DECISION_KINDS, resolveM4WorldDecision } from '../sim/m4/worldActions.js'; // M4-C

const ok = (msg) => ({ ok: true, msg });
const no = (msg) => ({ ok: false, msg });

const getWell = (state, id) => state.wells[id];
// Contract wells belong to the operator (§17): the driller can drill, case, trip and fish — nothing else.
const operatorWell = (w) => (w?.ownerOperatorId ? no(`${w.name} belongs to the operator — manage the job from the Contracts panel.`) : null);
export { acceptContract, abandonContract, acceptTip, dismissTip } from '../sim/contracts.js';
// cosmetics (rig skins & crew uniforms — cosmetic only; src/economy/cosmetics.js)
export { buyCosmetic, buyCosmeticBundle, equipSkin, equipUniform, setDefaultSkin, setDefaultUniform } from '../economy/cosmetics.js';

// ---- leases & surveys ---------------------------------------------------------------
export const leasePlot = (state, fieldId, plotIdx) => (M3O.leaseBlock(state, fieldId) ? no(M3O.leaseBlock(state, fieldId)) : leaseImpl(state, fieldId, plotIdx)); // M3 ops: service-only fields
export const sellLease = (state, fieldId, plotIdx) => sellLeaseImpl(state, fieldId, plotIdx);
export const survey = (state, fieldId, method) => (M3O.surveyBlock(state, fieldId, method) ? no(M3O.surveyBlock(state, fieldId, method)) : surveyImpl(state, fieldId, method)); // M3 ops: 3D needs a supercomputer

// ---- shop -----------------------------------------------------------------------------
export function canBuy(state, def) {
  if (!def) return false;
  if (!isUnlocked(state, def.id) && !M2R.rotaryKitUnlocked(state, def)) return false;
  if (def.obsoleteYear && yearOf(state) >= def.obsoleteYear && !state.research?.unlockedDefs?.[def.id]) return false;
  return true;
}

// opts: { fieldId } for tanks, { siteId } for forts, { rigUid } for per-rig safety gear,
//       { bootleg: true } M2 knock-off of a patented item (70% price, lawsuit risk)
// M2 wrapper: historical patent fees (Hughes bits, Halliburton cement jobs...) and knock-offs (sim/m2/licensing.js).
export function buyEquipment(state, defId, qty = 1, opts = {}) {
  const def = eqDef(defId);
  if (!def || !canBuy(state, def)) return buyEquipmentBase(state, defId, qty, opts);
  const n = Math.max(1, Math.round(qty));
  const pre = preBuy(state, def, n, opts ?? {});
  if (pre.ok === false) return pre;
  const r = buyEquipmentBase(state, defId, qty, opts);
  if (r.ok && pre.plan) r.msg += postBuy(state, def, pre.plan);
  return r;
}

function buyEquipmentBase(state, defId, qty = 1, opts = {}) {
  const def = eqDef(defId);
  if (!def) return no('Unknown item.');
  if (!canBuy(state, def)) return no(`${def.name} is not available in ${yearOf(state)}.`);
  qty = Math.max(1, Math.round(qty));
  const price = unitPrice(state, def);
  switch (def.cat) {
    case 'storage':
      if (!stat(def, 'consumable', false)) {
        const fid = opts.fieldId ?? Object.keys(state.fields).find((f) => Object.values(state.wells).some((w) => w.fieldId === f));
        if (!fid) return no('Choose a field to build the tank at.');
        return buildStorageImpl(state, fid, defId);
      }
      break;
    case 'security':
      if (!opts.siteId) return no('Choose a site to fortify.');
      return buildFortImpl(state, opts.siteId, defId);
    case 'safety':
      return buySafetyImpl(state, def, opts.rigUid ?? null);
    case 'lift':
      return no('Install pumps from the well panel.');
    case 'transport':
      return no('Set transport from the field panel.');
    case 'survey':
      return no('Surveys are ordered from the field panel.');
    case 'platform': case 'logistics': // M3 ops: platforms are built at a plot, services contracted to a rig/platform
      return M3O.buyRoute(state, def, opts);
    case 'completion': case 'water': { const r = shaleBuyRoute(state, def, qty, opts); if (r) return r; break; } // M4-A: frac services, proppant, water
    case 'midstream': case 'emissions': // M4-UI: plants & emissions kit are sited at a field (the gas / ESG sim reads items located there)
      if (opts.fieldId && state.fields[opts.fieldId]) {
        if (!spend(state, price * qty, 'capex')) return no(`${qty > 1 ? `${qty} × ` : ''}${def.name} costs ${fmt$(price * qty)}; not enough cash.`);
        for (let i = 0; i < qty; i++) addItem(state, defId, { type: 'field', id: opts.fieldId });
        return ok(`Built ${qty > 1 ? `${qty} × ` : ''}${def.name} at ${fieldDef(opts.fieldId)?.name ?? opts.fieldId} (${fmt$(price * qty)}).`);
      }
      break;
    case 'refinery':
      return no('Refinery units are built from the Refinery panel.');
    default:
      break;
  }
  const total = price * qty;
  if (!spend(state, total, def.cat === 'rig' || def.cat === 'engine' || def.cat === 'building' ? 'capex' : 'consumables')) return no(`${qty > 1 ? `${qty} × ` : ''}${def.name} costs ${fmt$(total)}; not enough cash.`);
  if (def.cat === 'rig') {
    const made = [];
    for (let i = 0; i < qty; i++) made.push(addRig(state, defId));
    // auto-fit a spare engine and bit
    for (const r of made) { autoEquipRig(state, r); M2R.autoEquipRotary(state, r); M3O.onRigBought(state, r); }
    // Tell the player exactly what's still needed before this rig can drill (it waits in the yard until assigned).
    const rs0 = rigStats(def);
    const roles = Object.entries(rs0.requiredRoles ?? {}).map(([role, n]) => `${n > 1 ? n + ' × ' : ''}${roleName(role)}`).join(', ');
    const steps = [
      rs0.needsEngine && !made[0].engineUid ? 'buy an engine (Shop → Engines; it fits itself)' : null,
      !made[0].bitUid ? 'buy a bit (Shop → Bits; it fits itself)' : null,
      roles ? `assign a crew of ${roles} (Crew panel)` : null,
      'then on Field pick a leased plot, choose this rig and press "Assign rig & spud"',
    ].filter(Boolean);
    return ok(`Bought ${made.map((r) => r.name).join(', ')} (${def.name}) — it's in your yard. Next: ${steps.join('; ')}.`);
  }
  if (isStackable(def)) {
    addStock(state, defId, qty);
    return ok(`Bought ${qty}${def.cat === 'line' || def.cat === 'casing' || def.cat === 'mud' ? ' ft' : def.cat === 'pipe' ? ' joints of' : ''} ${def.name} (${fmt$(total)}).`);
  }
  const items = [];
  for (let i = 0; i < qty; i++) items.push(addItem(state, defId, def.cat === 'building' ? { type: 'hq', id: null } : { type: 'warehouse', id: null }));
  if (def.cat === 'building') state.research.labs.push(...items.map((i) => i.uid));
  if (def.cat === 'engine') {
    for (const it of items) {
      const rig = Object.values(state.rigs).find((r) => rigStats(eqDef(r.defId)).needsEngine && !r.engineUid);
      if (rig) { installEngine(state, rig.uid, it.uid); }
    }
  }
  if (def.cat === 'bit') for (const r of Object.values(state.rigs).sort((a, b) => !!b.wellId - !!a.wellId)) if (!r.bitUid) autoEquipRig(state, r); // rigs on a well get first pick
  if (def.cat === 'rig_gear' || def.cat === 'bop') for (const it of items) M2R.autoMount(state, it, opts.rigUid ?? null);
  return ok(`Bought ${qty > 1 ? `${qty} × ` : ''}${def.name} (${fmt$(total)}).`);
}

function autoEquipRig(state, rig) {
  const rdef = eqDef(rig.defId);
  if (rigStats(rdef).needsEngine && !rig.engineUid) {
    const e = Object.values(state.items).find((i) => i.loc.type === 'warehouse' && eqDef(i.defId)?.cat === 'engine' && i.defId !== 'manpower');
    if (e) { e.loc = { type: 'rig', id: rig.uid }; rig.engineUid = e.uid; }
  }
  if (!rig.bitUid) {
    const b = bestSpareBit(state, rig);
    if (b) { b.loc = { type: 'rig', id: rig.uid }; rig.bitUid = b.uid; }
  }
}

export function sellItem(state, uid) {
  const rig = state.rigs[uid];
  if (rig) {
    if (rig.wellId) return no('Release the rig from its well first.');
    const v = unitPrice(state, eqDef(rig.defId)) * 0.5 * rig.condition / 100;
    for (const id of rig.crewIds) if (state.crew[id]) state.crew[id].rigId = null;
    if (rig.engineUid && state.items[rig.engineUid]) state.items[rig.engineUid].loc = { type: 'warehouse', id: null };
    if (rig.bitUid && state.items[rig.bitUid]) state.items[rig.bitUid].loc = { type: 'warehouse', id: null };
    M2R.unmountAll(state, rig);
    delete state.rigs[uid];
    state.company.cash += v;
    return ok(`Sold ${rig.name} for ${fmt$(v)}.`);
  }
  const it = state.items[uid];
  if (!it) return no('No such item.');
  if (it.loc.type !== 'warehouse' && it.loc.type !== 'hq') return no('Only items in the warehouse can be sold.');
  const v = unitPrice(state, eqDef(it.defId)) * 0.5 * it.condition / 100;
  delete state.items[uid];
  state.research.labs = state.research.labs.filter((l) => l !== uid);
  state.company.cash += v;
  return ok(`Sold ${eqDef(it.defId)?.name} for ${fmt$(v)}.`);
}

// ---- rigs & wells ---------------------------------------------------------------------
export function assignRig(state, rigUid, fieldId, plotIdx, { target } = {}) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (rigUnavailable(state, rig)) return no(rigUnavailable(state, rig));
  const f = state.fields[fieldId];
  if (!f || !fieldOpen(state, fieldId)) return no('That field is not open.');
  const blocked = regionBlock(state, fieldId); // M2: closed / occupied regions refuse new spuds
  if (blocked) return no(blocked);
  const plot = f.plots[plotIdx];
  if (!plot) return no('No such plot.');
  if (!plot.owned) return no('Lease the plot first.');
  const pad = shalePadSlot(state, fieldId, plotIdx); // M4-A: a shale DSU holds a pad of wells
  if (pad?.no) return no(pad.no);
  const m3a = M3O.preAssign(state, rig, fieldId, plotIdx, target); // M3 ops: water envelope, BOP, platform slot, ice season, tow (null before 1946)
  if (m3a && !m3a.ok) return m3a;
  const r37 = plot.wellId ? 0 : M2R.rule37Fee(state, fieldId, plotIdx); // M2 §13: Texas town-lot spacing permit
  if (r37 > 0 && !spend(state, r37, 'other')) return no(`A Rule 37 exception permit costs ${fmt$(r37)}.`);
  if (rig.wellId) {
    const cur = state.wells[rig.wellId];
    if (cur && ACTIVE_STATUSES.has(cur.status) && cur.status !== 'PAUSED') return no(`${rig.name} is busy at ${cur.name}.`);
    releaseRigImpl(state, rig);
  }
  autoEquipRig(state, rig);
  const rs = rigStats(eqDef(rig.defId));
  if (rs.needsEngine && !rig.engineUid) return no(`${rig.name} needs a steam engine installed.`);
  let well = plot.wellId && !pad?.newSlot ? state.wells[plot.wellId] : null; // M4-A: next pad slot → new well
  if (well) {
    const deepenable = ['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(well.status) && !well.contract && !well.ownerOperatorId;
    if (!['SUSPENDED', 'DRY', 'AT_TD'].includes(well.status) && !deepenable) return no(`${well.name} is ${well.status.toLowerCase().replace(/_/g, ' ')} — one well per plot.`);
    if (well.rigId && well.rigId !== rig.uid) return no(`${state.rigs[well.rigId]?.name ?? 'Another rig'} is on that well — release it first.`);
    if (rs.maxDepth <= well.depth + 1) return no(`${rig.name} can only reach ${rs.maxDepth} ft — ${well.name} is already ${Math.round(well.depth)} ft deep.`);
    if (deepenable) beginDeepening(state, well);
    well.rigId = rig.uid;
    if (target) well.target = Math.min(Math.max(target, well.depth + 1, well.firstPayFt && !well.reservoirId ? well.firstPayFt + 3 : 0), rs.maxDepth); // first well: not short of its pay
    else if (well.status !== 'SUSPENDED') well.target = Math.max(well.target, Math.min(rs.maxDepth, well.depth + 100));
  } else {
    const first = firstWellLuck(state, fieldId, plot);
    well = createWell(state, rig, fieldId, plotIdx, target);
    if (first) { well.firstPayFt = first; well.target = Math.max(well.target, Math.min(rs.maxDepth, first + 3)); } // never stop short of the pay
  }
  const m4RigUp = shaleOnAssign(state, rig, well, rs); // M4-A: pad build, walk/skid to the next slot, lateral plan (null otherwise)
  rig.at = { fieldId, plot: plotIdx };
  rig.wellId = well.id;
  well.drilledBy = rig.defId; // renderers: the derrick left standing (or none, for a spring pole) after the rig moves on
  well.bitUid = rig.bitUid;
  startRigUp(state, well, rig, m4RigUp ?? (m3a?.extraDays ? rs.rigUpDays + m3a.extraDays : null));
  if (m3a) M3O.afterAssign(state, rig, well, m3a);
  const capped = target && target > rs.maxDepth ? ` (${rig.name} only reaches ${rs.maxDepth} ft)` : '';
  const noBit = !rig.bitUid ? ` No bit for it in the warehouse — buy one in the Shop before it spuds.` : ''; // a Shop rig comes bare
  return ok(`${rig.name} rigging up on ${well.name}, target ${Math.round(well.target)} ft${capped}.${noBit}`);
}

// The company's first well in a new 1859 game always strikes oil by FIRST_WELL_PAY_FT (DESIGN §0, the 5-minute fun
// target): a dry plot, or one whose pay lies deeper, is moved onto the field's shallowest pay at the prospect's centre
// (depth jitter never deeper than the field average) with at least an average initial rate. Fields whose shallowest pay
// is deeper (Ploiești) get it lifted to just above the limit. Only games started from the new-game screen carry the
// state.firstWellLuck flag (sim tests and bots drill their own geology). Every well spudded before the company's first
// strike gets it — a rig released from the first plot and moved, or two rigs spudding at once, can't lose it — and it
// is spent once one of them has struck.
export const FIRST_WELL_PAY_FT = 80;
function firstWellLuck(state, fieldId, plot) {
  if (!state.firstWellLuck) return null;
  const struck = Object.values(state.wells).some((w) => !w.ownerOperatorId && (w.reservoirId || w.prod || w.status === 'PRODUCING'));
  if ((state.startYear ?? 1859) !== 1859 || struck) { state.firstWellLuck = false; return null; }
  const fdef = fieldDef(fieldId);
  const res = reservoirsAt(fdef, plot);
  if (res.length && res[0].top <= FIRST_WELL_PAY_FT - 4) return res[0].top; // a little margin: the strike comes a foot or so below the top
  let best = -1, bestTop = Infinity;
  (fdef.prospects ?? []).forEach((p, i) => { const t = Math.min(...(p.reservoirs ?? []).map((r) => r.top)); if (t < bestTop) { bestTop = t; best = i; } });
  if (best < 0) return null;
  plot.truth = { ...plot.truth, prospectIdx: best, dist: 0, edge: Math.min(plot.truth.edge ?? 0.3, 0.3), depthMult: Math.min(plot.truth.depthMult ?? 1, 1), qiRoll: Math.max(plot.truth.qiRoll ?? 0.5, 0.5) };
  if (bestTop > FIRST_WELL_PAY_FT - 4) plot.truth.payCap = FIRST_WELL_PAY_FT - 6;
  return reservoirsAt(fdef, plot)[0]?.top ?? null;
}

export function setTarget(state, wellId, depthFt) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (operatorWell(w)) return operatorWell(w);
  const rig = wellRig(state, w);
  const max = rig ? rigStats(eqDef(rig.defId)).maxDepth : depthFt;
  const t = Math.min(Math.max(depthFt, w.depth, w.firstPayFt && !w.reservoirId ? w.firstPayFt + 3 : 0), max); // first well: not short of its pay
  w.target = t;
  if (['AT_TD', 'DRY'].includes(w.status) && t > w.depth + 0.5 && rig) w.status = 'DRILLING';
  return ok(`Target ${Math.round(t)} ft${depthFt > max ? ` (rig limit ${max} ft)` : ''}.`);
}

export function startDrilling(state, wellId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (!wellRig(state, w)) return no('Assign a rig first.');
  if (w.status === 'PAUSED' || ((w.status === 'AT_TD' || w.status === 'DRY') && w.target > w.depth + 0.5)) { w.status = 'DRILLING'; return ok('Drilling.'); }
  return no(`Can't drill while ${w.status}.`);
}

export function pauseDrilling(state, wellId) {
  const w = getWell(state, wellId);
  if (!w || w.status !== 'DRILLING') return no('Not drilling.');
  w.status = 'PAUSED';
  return ok('Drilling paused.');
}

export function changeBit(state, wellId, bitUid) {
  const w = getWell(state, wellId);
  const rig = w && wellRig(state, w);
  if (!rig) return no('No rig on that well.');
  if (!['DRILLING', 'PAUSED'].includes(w.status)) return no(`Can't trip while ${w.status}.`);
  const b = bitUid ? state.items[bitUid] : bestSpareBit(state, rig);
  if (!b || b.loc.type !== 'warehouse') return no('No spare bit in the warehouse.');
  if (!bitFitsRig(eqDef(b.defId), eqDef(rig.defId))) return no("That bit doesn't fit this rig.");
  startBitChange(state, w, rig, b.uid);
  return ok(`Tripping out to change to the ${eqDef(b.defId).name}.`);
}

export function dressBit(state, wellId) {
  const w = getWell(state, wellId);
  const rig = w && wellRig(state, w);
  if (!rig) return no('No rig on that well.');
  if (!['DRILLING', 'PAUSED'].includes(w.status)) return no(`Can't dress while ${w.status}.`);
  const bit = state.items[rig.bitUid];
  if (!bit) return no('No bit on the rig.');
  if (w.rigType === 'rotary') return no('Rotary bits are run until dull and replaced, not dressed.');
  if (bit.maxCondition - 15 < 10) return no('This bit is worn past dressing — change it.');
  startDress(state, w, rig);
  return ok('Pulling tools to dress the bit.');
}

export function runCasing(state, wellId, defId, opts = {}) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  return runCasingImpl(state, w, defId, opts ?? {});
}

// driller's time: the player's touch on the brake today (share in the sweet spot, share bouncing the bit)
export const setDriveTouch = (state, wellId, q, bounce) => setDriveTouchImpl(state, wellId, q, bounce);
export const takeBrake = (state, wellId) => takeBrakeImpl(state, wellId);

export function shootTorpedo(state, wellId, defId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (operatorWell(w)) return operatorWell(w);
  return shootImpl(state, w, defId);
}

export function completeWell(state, wellId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (operatorWell(w)) return operatorWell(w);
  return completeImpl(state, w);
}

export function plugWell(state, wellId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (operatorWell(w)) return operatorWell(w);
  if (['PLUGGED'].includes(w.status)) return no('Already plugged.');
  if (['GUSHER'].includes(w.status)) return no("You can't plug a gusher until it's capped.");
  return plugImpl(state, w);
}

export function deepen(state, wellId, depthFt) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (operatorWell(w)) return operatorWell(w);
  if (!['DRY', 'AT_TD', 'SUSPENDED'].includes(w.status)) return no('Only dry or finished holes can be deepened.');
  const rig = wellRig(state, w);
  if (!rig) return no('Assign a rig to the plot to deepen it.');
  const max = rigStats(eqDef(rig.defId)).maxDepth;
  if (depthFt <= w.depth) return no('New target must be deeper.');
  w.target = Math.min(depthFt, max);
  w.status = 'DRILLING';
  w.problem = null;
  return ok(`Deepening ${w.name} to ${Math.round(w.target)} ft.`);
}

export const installLift = (state, wellId, defId) => operatorWell(state.wells[wellId]) ?? installLiftImpl(state, wellId, defId);
export const shutIn = (state, wellId, on) => operatorWell(state.wells[wellId]) ?? shutInImpl(state, wellId, on);

export function releaseRig(state, rigUid) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (!rig.wellId) return no(`${rig.name} is already in the yard.`);
  if (rig.contractId) return no(`${rig.name} is on contract — finish or abandon the job in the Contracts panel.`);
  const w = state.wells[rig.wellId];
  if (w?.status === 'GUSHER') return no('Not while the well is gushing!');
  if (['BLOWOUT', 'SHUT_IN_KICK', 'KILLING', 'STUCK', 'CEMENTING'].includes(w?.status)) return no(`Not while the well is ${w.status.toLowerCase().replace(/_/g, ' ')}.`);
  releaseRigImpl(state, rig);
  return ok(`${rig.name} back in the yard.${w && w.status === 'SUSPENDED' ? ` ${w.name} suspended at ${Math.round(w.depth)} ft.` : ''}`);
}

// Swap the rig on a well for one from the yard. Everything is checked before the old rig moves,
// so a refusal leaves the well exactly as it was. The old rig's hands walk over if the new rig has none.
const missingRoles = (state, rig) => {
  const need = { ...(rigStats(eqDef(rig.defId)).requiredRoles ?? {}) };
  for (const id of rig.crewIds ?? []) { const c = state.crew[id]; if (c?.status === 'active' && need[c.role]) need[c.role]--; }
  const miss = Object.entries(need).filter(([, n]) => n > 0).map(([r, n]) => `${n > 1 ? n + ' × ' : ''}${roleName(r)}`);
  return miss.length ? ` Still needs crew: ${miss.join(', ')} (Crew panel).` : '';
};
export function swapRig(state, wellId, newRigUid, targetFt = null) {
  const w = getWell(state, wellId);
  const nr = state.rigs[newRigUid];
  if (!w || !nr) return no('No such well or rig.');
  if (w.contract || w.ownerOperatorId) return no('Contract wells keep the operator\'s rig plan.');
  if (nr.wellId || nr.contractId) return no(`${nr.name} is already working.`);
  if (rigUnavailable(state, nr)) return no(rigUnavailable(state, nr));
  const rs = rigStats(eqDef(nr.defId));
  if (rs.maxDepth <= w.depth + 1) return no(`${nr.name} can only reach ${rs.maxDepth} ft — ${w.name} is already ${Math.round(w.depth)} ft deep.`);
  const spareEngine = Object.values(state.items).some((i) => i.loc.type === 'warehouse' && eqDef(i.defId)?.cat === 'engine' && i.defId !== 'manpower');
  if (rs.needsEngine && !nr.engineUid && !spareEngine) return no(`${nr.name} needs a steam engine — buy one in the Shop (Engines) first.`);
  const old = w.rigId ? state.rigs[w.rigId] : null;
  const okStatus = ['DRILLING', 'PAUSED', 'SUSPENDED', 'DRY', 'AT_TD', 'PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(w.status);
  if (!okStatus) return no(`Not while ${w.name} is ${w.status.toLowerCase().replace(/_/g, ' ')} — wait until the crew finish.`);
  const hands = old && !nr.crewIds?.length ? [...(old.crewIds ?? [])] : [];
  // the depth you're chasing (Well panel box); default: a good stretch deeper, capped at the rig's limit
  const want = Number.isFinite(+targetFt) && +targetFt > 0 ? +targetFt : Math.max(w.target ?? 0, Math.round(w.depth + 1000));
  const target = Math.min(rs.maxDepth, Math.max(Math.round(w.depth + 10), Math.round(want)));
  if (old) releaseRigImpl(state, old);
  for (const cid of hands) assignCrewImpl(state, cid, nr.uid);
  // the tools go with the job: the old rig's bit goes back on the rack so the new rig can fit it (if it suits)
  if (old && !nr.bitUid && old.bitUid && state.items[old.bitUid]) { state.items[old.bitUid].loc = { type: 'warehouse', id: null }; old.bitUid = null; }
  const r = assignRig(state, nr.uid, w.fieldId, w.plot, { target });
  if (!r.ok) return r;
  return ok(`${r.msg}${hands.length ? ` ${hands.length} crew moved across from ${old.name}.` : ''}${!nr.bitUid ? ' It has no bit that fits — buy one in the Shop.' : ''}${missingRoles(state, nr)}`);
}

export function installEngine(state, rigUid, engineUid) {
  const rig = state.rigs[rigUid];
  const e = state.items[engineUid];
  if (!rig || !e || eqDef(e.defId)?.cat !== 'engine') return no('Invalid rig or engine.');
  if (e.loc.type !== 'warehouse') return no('That engine is in use.');
  if (rig.engineUid && state.items[rig.engineUid]) state.items[rig.engineUid].loc = { type: 'warehouse', id: null };
  e.loc = { type: 'rig', id: rig.uid };
  rig.engineUid = e.uid;
  return ok(`${eqDef(e.defId).name} installed on ${rig.name}.`);
}

export function setWob(state, wellId, wob) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  const v = Math.min(WOB_MAX, Math.max(WOB_MIN, Number(wob) || 1));
  w.wob = Math.round(v * 100) / 100;
  const label = v < 0.8 ? 'Light' : v < 1.15 ? 'Normal' : v < 1.45 ? 'Heavy' : 'Ram it';
  return ok(`${w.name}: ${label} (${w.wob.toFixed(2)}).`);
}

export function straighten(state, wellId, method = 'straighten') {
  const w = getWell(state, wellId);
  if (!w || !wellRig(state, w)) return no('No rig on that well.');
  if (!['DRILLING', 'PAUSED'].includes(w.status)) return no(`Can't do that while ${w.status}.`);
  if ((w.deviationDeg ?? 0) < 3) return no('The hole is straight enough.');
  return startStraighten(state, w, method === 'sidetrack' ? 'sidetrack' : 'straighten');
}

export function setRigAuto(state, rigUid, key, value) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (key === 'program') return M2R.setDrillingProgram(state, rigUid, value);
  if (!(key in rig.auto)) return no(`Unknown setting ${key}.`);
  rig.auto[key] = typeof rig.auto[key] === 'number' ? Number(value) : !!value;
  return ok(`${rig.name}: ${key} = ${rig.auto[key]}.`);
}

// ---- crew ----------------------------------------------------------------------------
export const hireCrew = (state, candidateId) => hireImpl(state, candidateId);
export const fireCrew = (state, crewId) => fireImpl(state, crewId);
export const hireFireman = (state, rigUid) => hireFiremanImpl(state, rigUid); // a boiler tender straight onto a steam rig
export const assignCrew = (state, crewId, rigUid) => assignCrewImpl(state, crewId, rigUid);

export function setWage(state, role, multiplier) {
  if (!ROLES[role]) return no('Unknown role.');
  const m = Math.min(2, Math.max(0.6, Number(multiplier) || 1));
  state.company.wageMult[role] = m;
  return ok(`${ROLES[role].name} wages set to ${Math.round(m * 100)}% of market.`);
}

export function setTours(state, rigUid, n) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  rig.tours = Math.min(3, Math.max(1, Math.round(n)));
  return ok(`${rig.name}: ${rig.tours} tour${rig.tours > 1 ? 's' : ''} a day.`);
}

// ---- oil -----------------------------------------------------------------------------
export const setSellMode = (state, fieldId, mode, pct) => setSellModeImpl(state, fieldId, mode, pct);
export const sellOil = (state, fieldId, bbl) => sellOilImpl(state, fieldId, bbl);
export const buildStorage = (state, fieldId, defId) => buildStorageImpl(state, fieldId, defId);
export const setTransport = (state, fieldId, defId) => setTransportImpl(state, fieldId, defId);
export function setAutoBuyBarrels(state, fieldId, on) {
  const f = state.fields[fieldId];
  if (!f) return no('No such field.');
  f.autoBuyBarrels = !!on;
  return ok(`Auto-buy barrels ${on ? 'on' : 'off'}.`);
}

// ---- finance -------------------------------------------------------------------------
export const takeLoan = (state, amount) => takeLoanImpl(state, amount);
export const repayLoan = (state, loanId, amount) => repayLoanImpl(state, loanId, amount);

// ---- time ------------------------------------------------------------------------------
export function setSpeed(state, n) {
  state.speed = Math.min(4, Math.max(0, Math.round(n)));
  state.paused = state.speed === 0;
  return ok(state.paused ? 'Paused.' : `Speed ${state.speed}×.`);
}
export function togglePause(state) {
  state.paused = !state.paused;
  if (!state.paused && state.speed === 0) state.speed = 1;
  return ok(state.paused ? 'Paused.' : 'Running.');
}

// ---- security (§12) ----------------------------------------------------------------------
export const resolveIncident = (state, incidentId, choice) => resolveImpl(state, incidentId, choice);
export const assignGuard = (state, crewId, siteId) => assignGuardImpl(state, crewId, siteId);
export const buildFort = (state, siteId, defId) => buildFortImpl(state, siteId, defId);

// ---- research (§13) ------------------------------------------------------------------------
export const startResearch = (state, techId, funding = 'standard') => startResearchImpl(state, techId, funding);
export const setFunding = (state, techId, funding) => setFundingImpl(state, techId, funding);
export const cancelResearch = (state, techId) => cancelResearchImpl(state, techId);
export const assignInventor = (state, crewId, labUid) => assignInventorImpl(state, crewId, labUid);
export const resolveInvention = (state, techId, mode) => resolveInventionImpl(state, techId, mode);

// ---- safety (§15) --------------------------------------------------------------------------
export const setSafetyBudget = (state, rigUid, perDay) => setSafetyBudgetImpl(state, rigUid, perDay);
export const respondWarning = (state, warningId) => respondWarningImpl(state, warningId);
export const setBoilerSpacing = (state, rigUid, ft) => setBoilerSpacingImpl(state, rigUid, ft);
// Steam boiler gauges (sim/boiler.js): level 'bank' | 'steady' | 'hard' | 'crowd'
export const setFiringRate = (state, rigUid, level) => setFiringRateImpl(state, rigUid, level);
export const topUpBoiler = (state, rigUid) => topUpBoilerImpl(state, rigUid);
export function setWorkersCompPolicy(state, policy) {
  if (!['ask', 'always', 'never'].includes(policy)) return no("Policy must be 'ask', 'always' or 'never'.");
  state.safety.workersComp = policy;
  return ok(`Workers' compensation: ${policy}.`);
}

// Generic decision dispatcher for state.pendingDecision (incident | invention | workersComp).
export function resolveDecision(state, decisionId, choice) {
  const all = [state.pendingDecision, ...(state.decisionQueue ?? [])].filter(Boolean);
  const d = all.find((x) => x.id === decisionId);
  if (!d) return no('Nothing to decide.');
  if (d.kind === 'incident') return resolveImpl(state, d.incidentId, choice);
  if (d.kind === 'invention') return resolveInventionImpl(state, d.techId, choice);
  if (d.kind === 'workersComp') return resolveCompImpl(state, d.id, choice);
  if (d.kind === 'standardOffer') return respondStandardOfferImpl(state, d.id, choice);
  if (d.kind === 'tankFire') return resolveTankFireImpl(state, d.id, choice);
  if (d.kind === 'gusher') return M2R.resolveGusher(state, d.id, choice);
  if (M3O.M3_OPS_DECISION_KINDS.has(d.kind)) return M3O.resolveM3OpsDecision(state, d, choice); // M3 ops: storm (evacuate | ride)
  if (M2_DECISION_KINDS.has(d.kind)) return resolveM2Decision(state, d, choice); // kidnapping, laborStrike, partnerOffer, infringement, patentSuit
  if (M3E.M3_ECON_DECISION_KINDS.has(d.kind)) return M3E.resolveM3EconDecision(state, d, choice); // M3 econ: participation, wellFireContract
  if (M4W_DECISION_KINDS.has(d.kind)) return resolveM4WorldDecision(state, d, choice); // M4-C: sign-or-be-seized (Venezuela 2007)
  popDecision(state, d.id);
  return ok('Dismissed.');
}

// ---- tokens (MONETIZATION.md) ----------------------------------------------------------------
export function tokenCost(state, timerId, opts = {}) {
  return tokens.rushCost(state, timerId, opts);
}

export function rushTimer(state, timerId, { half = false } = {}) {
  const t = state.timers[timerId];
  if (!t) return no('Nothing to rush.');
  if (t.rushable === false) return no("That can't be rushed.");
  const cost = tokens.rushCost(state, t, { half });
  const pay = tokens.spend(state, cost, `rush:${t.kind}`, t.id);
  if (!pay.ok) return pay;
  t.tokensSpent = (t.tokensSpent ?? 0) + cost;
  if (half && t.daysLeft > 1) {
    if (t.kind === 'RESEARCH') {
      const p = state.research.projects[t.data.techId];
      if (p) p.progressDays += 0.5 * Math.max(0, p.totalDays - p.progressDays);
    }
    t.daysLeft = Math.ceil(t.daysLeft / 2);
    return ok(`Half-rushed for ${cost} ◈: ${t.daysLeft} day(s) left.`);
  }
  finishTimer(state, t, true);
  return ok(`Rushed for ${cost} ◈.`);
}

export function devGrantTokens(state, n = 100) {
  return tokens.grant(state, n, 'dev');
}

export function claimDaily(state, today) {
  return tokens.claimDaily(state, today);
}

function paySink(state, sku, ref, ctx) {
  const cost = tokens.sinkCost(state, sku, ctx);
  return { cost, res: tokens.spend(state, cost, sku, ref) };
}

// Drill to target now: skips risk rolls but still consumes bits, rope, casing, fuel & wages.
export function rushDrillEstimate(state, wellId) {
  const w = getWell(state, wellId);
  const rig = w && wellRig(state, w);
  if (!rig || w.status !== 'DRILLING') return null;
  const strata = strataAt(fieldDef(w.fieldId), state.fields[w.fieldId].plots[w.plot]);
  const rop = ropAt(state, w, stratumAt(strata, w.depth + 0.01));
  const days = rop > 0 ? Math.ceil((w.target - w.depth) / rop * 1.15) : 0;
  return { days, cost: tokens.rushPrice(tokens.TOKEN_SINKS.rush_drill.k, days) };
}

export function rushDrill(state, wellId) {
  const est = rushDrillEstimate(state, wellId);
  if (!est || est.days <= 0) return no('The well must be drilling with nothing blocking it.');
  const w = getWell(state, wellId);
  if (w.problem) return no(`Fix the problem first: ${w.problem.text}`);
  const pay = tokens.spend(state, est.cost, 'rush_drill', wellId);
  if (!pay.ok) return pay;
  const rig = wellRig(state, w);
  state._rush = true;
  let days = 0;
  try {
    while (days < 400 && w.status !== 'AT_TD' && w.status !== 'DRY' && w.status !== 'PRODUCING' && w.status !== 'COMPLETING' && !w.problem) {
      if (w.timerId && state.timers[w.timerId]) {
        const t = state.timers[w.timerId];
        if (t.kind === 'CAPPING') break;
        finishTimer(state, t, true);
        continue;
      }
      if (w.status !== 'DRILLING') break;
      drillDay(state, w);
      days++;
      // wages for the compressed days
      for (const c of activeRigCrew(state, rig)) spend(state, crewWage(state, c), 'wages', false);
    }
  } finally {
    delete state._rush;
  }
  return ok(`Drilled ${days} day(s) of hole for ${est.cost} ◈ — ${w.name} at ${Math.round(w.depth)} ft${w.problem ? ` (stopped: ${w.problem.text})` : ''}.`);
}

export function eureka(state, techId) {
  if (!state.research.projects[techId]) return no('No such project.');
  const { res } = paySink(state, 'eureka', techId);
  return res.ok ? eurekaImpl(state, techId) : res;
}

export function rerollSetback(state, techId) {
  const p = state.research.projects[techId];
  if (!p?.lastSetback || state.day - p.lastSetback.day > 30) return no('No recent setback to undo.');
  const { res } = paySink(state, 'reroll_setback', techId);
  return res.ok ? rerollSetbackImpl(state, techId) : res;
}

export function buyLabSlot(state) {
  if ((state.research.extraSlots ?? 0) >= 1) return no('Maximum extra slots reached.');
  const { res } = paySink(state, 'extra_lab_slot', null);
  if (!res.ok) return res;
  state.research.extraSlots = (state.research.extraSlots ?? 0) + 1;
  return ok('Extra lab slot unlocked.');
}

export function emergencyHire(state, role) {
  if (!ROLES[role]) return no('Unknown role.');
  const { res } = paySink(state, 'emergency_hire', role);
  if (!res.ok) return res;
  const c = makeCrew(state, role, 60 + Math.floor((state.rng % 25)));
  state.hirePool.unshift(c);
  return hireImpl(state, c.id);
}

export function crewBarbecue(state, rigUid) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  const { res } = paySink(state, 'barbecue', rigUid);
  if (!res.ok) return res;
  for (const c of activeRigCrew(state, rig)) c.morale = Math.min(100, c.morale + 30);
  return ok(`Steaks and whiskey for the ${rig.name} crew. Morale +30.`);
}

export function revealPlot(state, fieldId, plotIdx) {
  const p = state.fields[fieldId]?.plots[plotIdx];
  if (!p) return no('No such plot.');
  const { res } = paySink(state, 'reveal_plot', `${fieldId}:${plotIdx}`);
  if (!res.ok) return res;
  p.survey = { est: p.truth.prospectIdx >= 0 ? 1 : 0, sigma: 0.01, methods: [...(p.survey?.methods ?? []), 'premium'] };
  return ok(p.truth.prospectIdx >= 0 ? 'Oil is down there.' : 'Bone dry. Save your money.');
}

export function revealField(state, fieldId) {
  const f = state.fields[fieldId];
  if (!f) return no('No such field.');
  if (!(f.surveysDone ?? []).length) return no("Do the era's best survey first.");
  const { res } = paySink(state, 'reveal_field', fieldId);
  if (!res.ok) return res;
  for (const p of f.plots) p.survey = { est: p.truth.prospectIdx >= 0 ? 1 : 0, sigma: 0.01, methods: [...(p.survey?.methods ?? []), 'premium'] };
  return ok(`${fieldDef(fieldId).name} fully mapped.`);
}

export function hireMercenaries(state, regionId) {
  if (!regionDef(regionId)) return no('No such region.');
  const { res } = paySink(state, 'mercenaries', regionId);
  if (!res.ok) return res;
  const reg = state.regions[regionId];
  reg.mercUntil = Math.max(reg.mercUntil ?? 0, state.day) + 90;
  return ok('Hired guns patrol your sites for 90 days.');
}

// Lloyd's token policy: pays 100% of seized book value on expropriation (sim/m2/politics.js seizeRegion)
export function buyLloydsPolicy(state, regionId) {
  if (!regionDef(regionId)) return no('No such region.');
  const risk = effectiveRisk(state, regionId);
  const tier = risk.security >= 0.9 ? 0 : risk.security >= 0.7 ? 1 : risk.security >= 0.5 ? 2 : risk.security >= 0.25 ? 3 : 4;
  const { res } = paySink(state, 'lloyds_policy', regionId, { riskTier: tier });
  if (!res.ok) return res;
  state.tokens.insurance[regionId] = { kind: 'lloyds', untilDay: state.day + 365 };
  return ok("Lloyd's policy in force for a year.");
}

// ---- refining (DESIGN §19) -----------------------------------------------------------
export const buildRefinery = (state, siteId, opts) => buildRefineryImpl(state, siteId, opts);
export const addUnit = (state, refUid, unitId) => addUnitImpl(state, refUid, unitId);
export const setThroughput = (state, refUid, bblPerDay) => setThroughputImpl(state, refUid, bblPerDay);
export const setFireTest = (state, refUid, degF) => setFireTestImpl(state, refUid, degF);
export const buyCrude = (state, refUid, bbl, grade) => buyCrudeImpl(state, refUid, bbl, grade);
export const routeCrude = (state, fieldId, refUid) => routeCrudeImpl(state, fieldId, refUid);
export const sellProduct = (state, refUid, productId, qty) => sellProductImpl(state, refUid, productId, qty);
export const setProductSellMode = (state, refUid, productId, mode, pct) => setProductSellModeImpl(state, refUid, productId, mode, pct);
export const respondStandardOffer = (state, offerId, choice) => respondStandardOfferImpl(state, offerId, choice);
export const setAutoBuyCrude = (state, refUid, on, maxPrice, grade, fillPct) => setAutoBuyCrudeImpl(state, refUid, on, maxPrice, grade, fillPct);
export const setRefinerySafety = (state, refUid, perDay) => setRefinerySafetyImpl(state, refUid, perDay);
export const setBurnResiduum = (state, refUid, on) => setBurnResiduumImpl(state, refUid, on);

// ---- riding out a bust (sim/austerity.js) --------------------------------------------------
export const standDown = (state, crewId) => standDownImpl(state, crewId);
export const recallCrew = (state, crewId) => recallImpl(state, crewId);
export const mothballRig = (state, rigUid) => mothballImpl(state, rigUid);
export const reactivateRig = (state, rigUid) => reactivateImpl(state, rigUid);

// ---- M2 economy & politics (sim/m2/*) -------------------------------------------------------
// prorationing & hot oil (§7)
export const setProduction = (state, wellId, opts) => setProductionImpl(state, wellId, opts);
export const setHotOil = (state, fieldId, on, mult = 1) => setHotOilImpl(state, fieldId, on, mult);
// politics (§10): local partners, bribes, political-risk insurance
export const setPartner = (state, regionId, partnerId) => setPartnerImpl(state, regionId, partnerId);
export const payBribe = (state, regionId, perMonth) => payBribeImpl(state, regionId, perMonth);
export const buyInsurance = (state, regionId, opts = {}) => buyInsuranceImpl(state, regionId, opts);
export const cancelInsurance = (state, regionId) => cancelInsuranceImpl(state, regionId);
// logging & coring (§12); field surveys (torsion balance, seismic) go through survey(state, fieldId, method)
export const runElog = (state, wellId) => runElogImpl(state, wellId);
export const coreWell = (state, wellId) => coreWellImpl(state, wellId);
export const orderSurvey = (state, fieldId, method) => survey(state, fieldId, method);
// licensing & lawsuits (§14)
export const buyLicense = (state, techId) => buyLicenseImpl(state, techId);
export const licenseTech = buyLicense;
export const fileSuit = (state, techId) => fileSuitImpl(state, techId);
export const settleSuit = (state, suitId) => settleSuitImpl(state, suitId);

// ---- M3 economy & politics (sim/m3/econ*.js) ------------------------------------------------
export const placeBid = (state, roundId, tract, amount, opts) => M3E.placeBid(state, roundId, tract, amount, opts);
export const bidLeaseRound = placeBid;
export const applyLicence = (state, roundId, tract, wells) => M3E.applyLicence(state, roundId, tract, wells);
export const withdrawBid = (state, roundId, tract) => M3E.withdrawBid(state, roundId, tract);
export const acceptConcession = (state, fieldId) => M3E.acceptConcession(state, fieldId);
export const setBuyback = (state, regionId, on) => M3E.setBuyback(state, regionId, on);
export const sellForward = (state, opts) => M3E.sellForward(state, opts);
export const openFuture = (state, opts) => M3E.openFuture(state, opts);
export const buyPut = (state, opts) => M3E.buyPut(state, opts);
export const closeHedge = (state, positionId) => M3E.closeHedge(state, positionId);
export const closeFuture = closeHedge;
export const chooseRoute = (state, fieldId, choice) => M3E.chooseRoute(state, fieldId, choice);
export const prepareSafetyCase = (state, fieldId) => M3E.prepareSafetyCase(state, fieldId);

// ---- M2 rotary drilling (M2_SPEC §1–§6, §11, §13; see sim/m2actions.js) ----------------------------
export const setRpm = (state, wellId, x) => M2R.setRpm(state, wellId, x);
export const setMud = (state, wellId, defId, ppg) => M2R.setMud(state, wellId, defId, ppg);
export const setMudWeight = (state, wellId, ppg) => M2R.setMudWeight(state, wellId, ppg);
export const setMudType = (state, wellId, defId) => M2R.setMudType(state, wellId, defId);
export const installGear = (state, rigUid, itemUid) => M2R.installGear(state, rigUid, itemUid);
export const removeGear = (state, rigUid, slot) => M2R.removeGear(state, rigUid, slot);
export const installBop = (state, rigUid, itemUid) => M2R.installGear(state, rigUid, itemUid);
export const testBop = (state, rigUid) => M2R.testBop(state, rigUid);
export const cementCasing = (state, wellId, cementDefId) => M2R.cementCasing(state, wellId, cementDefId);
export const killWell = (state, wellId) => M2R.killWell(state, wellId);
export const workPipe = (state, wellId) => M2R.workPipe(state, wellId);
export const fish = (state, wellId) => M2R.fish(state, wellId);
export const sidetrack = (state, wellId) => M2R.sidetrack(state, wellId);
export const inspectPipe = (state, pipeId) => M2R.inspectPipe(state, pipeId);
export const contractDrill = (state, fieldId, plotIdx, targetFt) => (regionBlock(state, fieldId) ? no(regionBlock(state, fieldId)) : M2R.contractDrill(state, fieldId, plotIdx, targetFt));
export const recomplete = (state, wellId, resId) => M2R.recomplete(state, wellId, resId);
export const assignToolpusher = (state, crewId, rigUids) => M2R.assignToolpusher(state, crewId, rigUids);
export const setDrillingProgram = (state, rigUid, program) => M2R.setDrillingProgram(state, rigUid, program);
export const programTemplate = (state, fieldId) => M2R.programTemplate(state, fieldId);
export function moveRig(state, rigUid, plotIdx) {
  const pre = M2R.checkMoveRig(state, rigUid, plotIdx);
  if (!pre.ok) return pre;
  const r = assignRig(state, rigUid, pre.fieldId, plotIdx, { target: pre.target });
  if (!r.ok) return r;
  return M2R.afterMoveRig(state, rigUid, r);
}

// ---- M3 offshore operations (sim/m3/ops*) ----------------------------------------------------------------------
export const towRig = (state, rigUid, fieldId, plotIdx, opts = {}) => assignRig(state, rigUid, fieldId, plotIdx, opts);
export const stackRig = (state, rigUid, on = true) => M3O.stackRig(state, rigUid, on);
export const charterRig = (state, defId, fieldId, plotIdx, opts = {}) => (regionBlock(state, fieldId) ? no(regionBlock(state, fieldId)) : M3O.charterRig(state, defId, fieldId, plotIdx, opts));
export const releaseCharter = (state, wellId) => M3O.releaseCharter(state, wellId);
export const acceptCharterOut = (state, offerId, rigUid) => M3O.acceptCharterOut(state, offerId, rigUid);
export const buildPlatform = (state, fieldId, plotIdx, defId) => M3O.buildPlatform(state, fieldId, plotIdx, defId);
export const connectPlatform = (state, platformUid, transportId) => M3O.connectPlatform(state, platformUid, transportId);
export const installSubsea = (state, wellId, defId = 'subsea_tree', hostUid = null) => M3O.installSubsea(state, wellId, defId, hostUid);
export const removePlatform = (state, platformUid) => M3O.removePlatform(state, platformUid);
export const contractService = (state, defId, targetUid) => M3O.contractService(state, defId, targetUid);
export const releaseService = (state, uid) => M3O.releaseService(state, uid);
export const startEor = (state, fieldId, resId, method) => M3O.startEor(state, fieldId, resId, method);
export const joinMegaproject = (state, id, share) => M3O.joinMegaproject(state, id, share);
export const setHorizontal = (state, wellId, lateralFt) => M3O.setHorizontal(state, wellId, lateralFt);

// M4 (2000–2029) player actions
export * from '../sim/m4/actions.js';
